'use client';
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './CustomDatePicker.module.css';
import { useLanguage } from '@/context/LanguageContext';

/** Map app locale to BCP 47 (ua → uk for Intl). */
function toIntlLocale(appLocale) {
  if (!appLocale) return 'lt';
  if (appLocale === 'ua') return 'uk';
  return appLocale;
}

/** Parse YYYY-MM-DD as a local calendar date (avoid UTC midnight shifting the day). */
function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const ymd = String(value).split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Month names + Mon–Sun short labels for the calendar header (week starts Monday). */
function getCalendarLabels(intlLocale) {
  const months = Array.from({ length: 12 }, (_, monthIndex) => {
    const d = new Date(2000, monthIndex, 1);
    return new Intl.DateTimeFormat(intlLocale, { month: 'long' }).format(d);
  });
  const monday = new Date(2024, 0, 1);
  const weekdays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return new Intl.DateTimeFormat(intlLocale, { weekday: 'short' }).format(d);
  });
  return { months, weekdays };
}

export default function CustomDatePicker({
  value,
  onChange,
  placeholder = 'YYYY-MM-DD',
  minDate,
  maxDate,
  disabled = false,
  error = false,
  className = '',
  /** 44px field height — matches account filter selects */
  compact = false,
  defaultYear = null, // Optional: specify default year when picker opens without a value
  /** Always show calendar (no trigger click). */
  inline = false,
  /** Bottom-sheet title on mobile (falls back to placeholder / “Select date”). */
  title = '',
  ...props
}) {
  const { locale: appLocale, t } = useLanguage();
  const { months: monthNames, weekdays: weekdayLabels } = useMemo(
    () => getCalendarLabels(toIntlLocale(appLocale)),
    [appLocale]
  );

  const [isOpen, setIsOpen] = useState(inline);
  const [selectedDate, setSelectedDate] = useState(() => parseDateValue(value));
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const useMobileSheet = isMobile && !inline;

  // Ensure current year respects minDate and maxDate constraints
  const todayYear = new Date().getFullYear();
  let minYear, maxYear;

  if (maxDate && !minDate) {
    // Birth date case: maxDate is today
    maxYear = maxDate.getFullYear();
    minYear = maxYear - 100; // 100 years back
  } else if (minDate && !maxDate) {
    // Future date case: minDate is today
    minYear = minDate.getFullYear();
    maxYear = minYear + 5; // 5 years forward
  } else if (minDate && maxDate) {
    // Both constraints
    minYear = minDate.getFullYear();
    maxYear = maxDate.getFullYear();
  } else {
    // No constraints
    minYear = todayYear;
    maxYear = todayYear + 5;
  }

  const clampYear = y => Math.max(Math.min(y, maxYear), minYear);

  const getMonthRangeForYear = year => {
    let startMonth = 0;
    let endMonth = 11;
    if (minDate && year === minDate.getFullYear()) {
      startMonth = minDate.getMonth();
    }
    if (maxDate && year === maxDate.getFullYear()) {
      endMonth = maxDate.getMonth();
    }
    return { startMonth, endMonth: Math.max(startMonth, endMonth) };
  };

  const clampMonthForYear = (year, month) => {
    const { startMonth, endMonth } = getMonthRangeForYear(year);
    return Math.max(startMonth, Math.min(month, endMonth));
  };

  const syncCalendarView = (date, fallbackMonth = new Date().getMonth()) => {
    if (date) {
      const year = clampYear(date.getFullYear());
      setCurrentYear(year);
      setCurrentMonth(clampMonthForYear(year, date.getMonth()));
      return;
    }
    const year =
      defaultYear !== null ? clampYear(defaultYear) : clampYear(todayYear);
    setCurrentYear(year);
    setCurrentMonth(clampMonthForYear(year, fallbackMonth));
  };

  const minDateTimestamp = minDate?.getTime() ?? null;

  // With no value: default to current calendar year (clamped), not the max allowed year
  const initialYear = selectedDate
    ? clampYear(selectedDate.getFullYear())
    : defaultYear !== null
      ? clampYear(defaultYear)
      : clampYear(todayYear);
  const [currentYear, setCurrentYear] = useState(initialYear);
  const [currentMonth, setCurrentMonth] = useState(() =>
    clampMonthForYear(
      initialYear,
      selectedDate ? selectedDate.getMonth() : new Date().getMonth()
    )
  );

  const pickerRef = useRef(null);
  const popupRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const monthDropdownRef = useRef(null);

  // Generate years based on minDate and maxDate constraints
  const generateYears = () => {
    let startYear, endYear;

    if (maxDate && !minDate) {
      // Birth date case: maxDate is today, show reasonable past range
      endYear = maxDate.getFullYear();
      startYear = endYear - 100; // Show 100 years back (reasonable for birth dates)
    } else if (minDate && !maxDate) {
      // Future date case: minDate is today, show current + future years
      startYear = minDate.getFullYear();
      endYear = startYear + 5; // Show 5 years into future
    } else if (minDate && maxDate) {
      // Both constraints: use the actual range
      startYear = minDate.getFullYear();
      endYear = maxDate.getFullYear();
    } else {
      // No constraints: show current year + 5 future years
      startYear = todayYear;
      endYear = todayYear + 5;
    }

    const yearRange = endYear - startYear + 1;
    const yearsArray = Array.from(
      { length: Math.max(yearRange, 1) },
      (_, i) => startYear + i
    );
    // Reverse to show newest to oldest when selecting past dates (maxDate is set)
    // For future dates, keep ascending order
    if (maxDate) {
      return yearsArray.reverse();
    }
    return yearsArray;
  };

  const years = generateYears();

  // Update selected date when value prop changes
  useEffect(() => {
    const parsed = parseDateValue(value);
    if (parsed) {
      setSelectedDate(parsed);
      syncCalendarView(parsed);
    } else {
      setSelectedDate(null);
      syncCalendarView(minDate);
    }
  }, [value, minYear, maxYear, defaultYear, todayYear, minDateTimestamp]);

  // Handle click outside to close picker (portal calendar lives on document.body)
  useEffect(() => {
    if (inline || useMobileSheet) return undefined;
    const handleClickOutside = event => {
      const target = event.target;
      if (pickerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setIsOpen(false);
      setShowYearDropdown(false);
      setShowMonthDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [inline, useMobileSheet]);

  // Portal calendar: pin to the viewport, flip above the field if it would be clipped
  useLayoutEffect(() => {
    if (inline || !isOpen || useMobileSheet) return undefined;
    const popup = popupRef.current;
    const trigger = pickerRef.current;
    if (!popup || !trigger) return undefined;

    const margin = 8;
    let didScrollIntoView = false;

    const place = () => {
      const triggerRect = trigger.getBoundingClientRect();
      const maxWidth = Math.min(340, window.innerWidth - margin * 2);
      const width = Math.min(Math.max(triggerRect.width, 280), maxWidth);

      popup.style.position = 'fixed';
      popup.style.width = `${width}px`;
      popup.style.maxWidth = `${maxWidth}px`;
      popup.style.marginTop = '0px';
      popup.style.right = 'auto';
      popup.style.zIndex = '1200';

      let left = triggerRect.left;
      if (left + width > window.innerWidth - margin) {
        left = window.innerWidth - width - margin;
      }
      if (left < margin) left = margin;
      popup.style.left = `${left}px`;

      const spaceBelow = window.innerHeight - triggerRect.bottom - margin;
      const spaceAbove = triggerRect.top - margin;
      const popupHeight = popup.offsetHeight;
      const openUp = popupHeight > spaceBelow && spaceAbove > spaceBelow;

      if (popupHeight > Math.max(spaceBelow, spaceAbove)) {
        popup.style.maxHeight = `${window.innerHeight - margin * 2}px`;
        popup.style.overflowY = 'auto';
      } else {
        popup.style.maxHeight = '';
        popup.style.overflowY = '';
      }

      if (openUp) {
        popup.style.top = 'auto';
        popup.style.bottom = `${window.innerHeight - triggerRect.top + margin}px`;
      } else {
        popup.style.bottom = 'auto';
        popup.style.top = `${triggerRect.bottom + margin}px`;
      }

      const placed = popup.getBoundingClientRect();
      if (placed.top < margin) {
        popup.style.bottom = 'auto';
        popup.style.top = `${margin}px`;
      } else if (placed.bottom > window.innerHeight - margin) {
        popup.style.top = 'auto';
        popup.style.bottom = `${margin}px`;
      }
    };

    place();
    const placed = popup.getBoundingClientRect();
    if (
      !didScrollIntoView &&
      (placed.bottom > window.innerHeight - margin || placed.top < margin)
    ) {
      didScrollIntoView = true;
      trigger.scrollIntoView({ block: 'center', inline: 'nearest' });
      place();
    }

    const onWin = () => place();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [
    isOpen,
    inline,
    currentMonth,
    currentYear,
    showYearDropdown,
    showMonthDropdown,
    useMobileSheet,
  ]);

  useEffect(() => {
    if (!useMobileSheet || !isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowYearDropdown(false);
        setShowMonthDropdown(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [useMobileSheet, isOpen]);

  useLayoutEffect(() => {
    if (!showYearDropdown) return;
    const selected = yearDropdownRef.current?.querySelector(
      `.${styles.selected}`
    );
    selected?.scrollIntoView({ block: 'nearest' });
  }, [showYearDropdown, currentYear]);

  useLayoutEffect(() => {
    if (!showMonthDropdown) return;
    const selected = monthDropdownRef.current?.querySelector(
      `.${styles.selected}`
    );
    selected?.scrollIntoView({ block: 'nearest' });
  }, [showMonthDropdown, currentMonth]);

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Find the first Monday of the week containing the first day of the month
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysToMonday = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to Monday-based week

    const firstMonday = new Date(firstDay);
    firstMonday.setDate(firstDay.getDate() - daysToMonday);

    const days = [];
    const currentDate = new Date(firstMonday);

    // Generate 6 rows of 7 days each (42 days total)
    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        const dayNumber = currentDate.getDate();
        const isCurrentMonth = currentDate.getMonth() === currentMonth;
        const isSelected =
          selectedDate &&
          isCurrentMonth &&
          dayNumber === selectedDate.getDate() &&
          currentDate.getMonth() === selectedDate.getMonth() &&
          currentDate.getFullYear() === selectedDate.getFullYear();

        // Check if date is disabled (outside min/max range)
        // Compare only date parts, ignore time
        const isDisabled = (() => {
          if (!minDate && !maxDate) return false;

          const currentDateOnly = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate()
          );

          if (minDate) {
            const minDateOnly = new Date(
              minDate.getFullYear(),
              minDate.getMonth(),
              minDate.getDate()
            );
            if (currentDateOnly < minDateOnly) return true;
          }

          if (maxDate) {
            const maxDateOnly = new Date(
              maxDate.getFullYear(),
              maxDate.getMonth(),
              maxDate.getDate()
            );
            if (currentDateOnly > maxDateOnly) return true;
          }

          return false;
        })();

        weekDays.push({
          number: dayNumber,
          date: new Date(currentDate),
          isCurrentMonth,
          isSelected,
          isDisabled,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }
      days.push(weekDays);
    }

    return days;
  };

  const handleDateSelect = day => {
    if (!day.isCurrentMonth || day.isDisabled) return;

    const newDate = new Date(currentYear, currentMonth, day.number);
    setSelectedDate(newDate);

    // Format date as YYYY-MM-DD (avoid timezone issues)
    const formatted = formatDateLocal(newDate);
    onChange(formatted);
    if (!inline) setIsOpen(false);
  };

  const handleYearSelect = year => {
    setCurrentYear(year);
    setCurrentMonth(prev => clampMonthForYear(year, prev));
    setShowYearDropdown(false);
  };

  const handleMonthSelect = month => {
    setCurrentMonth(month);
    setShowMonthDropdown(false);
  };

  const { startMonth, endMonth } = getMonthRangeForYear(currentYear);
  const availableMonths = Array.from(
    { length: endMonth - startMonth + 1 },
    (_, i) => startMonth + i
  );

  // Format date as YYYY-MM-DD without timezone conversion
  const formatDateLocal = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = date => {
    if (!date) return '';
    return formatDateLocal(date);
  };

  const calendarDays = generateCalendarDays();

  return (
    <div
      className={`${styles.datePickerContainer} ${inline ? styles.datePickerInline : ''} ${isOpen ? styles.datePickerContainerOpen : ''} ${className}`}
      ref={pickerRef}
    >
      {!inline ? (
        <div
          className={`${styles.inputField} ${error ? styles.inputError : ''} ${compact ? styles.inputFieldCompact : ''}`}
          onClick={() => {
            if (!disabled) {
              const willOpen = !isOpen;
              if (willOpen) {
                syncCalendarView(selectedDate || minDate);
              }
              setIsOpen(!isOpen);
            }
          }}
        >
          <div
            className={`${styles.calendarIcon} ${compact ? styles.calendarIconCompact : ''}`}
          >
            <svg
              width={compact ? 18 : 20}
              height={compact ? 18 : 20}
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M17.5 8.33366H2.5M13.3333 1.66699V5.00033M6.66667 1.66699V5.00033M8.75 11.667L10 10.8337V15.0003M8.95833 15.0003H11.0417M6.5 18.3337H13.5C14.9001 18.3337 15.6002 18.3337 16.135 18.0612C16.6054 17.8215 16.9878 17.439 17.2275 16.9686C17.5 16.4339 17.5 15.7338 17.5 14.3337V7.33366C17.5 5.93353 17.5 5.23346 17.2275 4.69868C16.9878 4.22828 16.6054 3.84583 16.135 3.60614C15.6002 3.33366 14.9001 3.33366 13.5 3.33366H6.5C5.09987 3.33366 4.3998 3.33366 3.86502 3.60614C3.39462 3.84583 3.01217 4.22828 2.77248 4.69868C2.5 5.23346 2.5 5.93353 2.5 7.33366V14.3337C2.5 15.7338 2.5 16.4339 2.77248 16.9686C3.01217 17.439 3.39462 17.8215 3.86502 18.0612C4.3998 18.3337 5.09987 18.3337 6.5 18.3337Z'
                stroke='#9AA4B2'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <input
            type='text'
            value={formatDisplayDate(parseDateValue(value) || selectedDate)}
            placeholder={placeholder}
            readOnly
            disabled={disabled}
            className={`${styles.input} ${compact ? styles.inputCompact : ''}`}
            suppressHydrationWarning
          />
          <div
            className={`${styles.calendarArrow} ${compact ? styles.calendarArrowCompact : ''}`}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M5 7.5L10 12.5L15 7.5'
                stroke='#9AA4B2'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      ) : null}

      {(() => {
        if (!isOpen && !inline) return null;
        const sheetTitle =
          title ||
          (placeholder && placeholder !== 'YYYY-MM-DD' ? placeholder : '') ||
          t('common.select_date');
        const closeSheet = () => {
          setIsOpen(false);
          setShowYearDropdown(false);
          setShowMonthDropdown(false);
        };
        const panel = (
        <div
          className={`${styles.datePicker} ${useMobileSheet ? styles.datePickerSheet : !inline ? styles.datePickerPortal : ''}`}
          ref={popupRef}
        >
          {/* Header with Year and Month dropdowns */}
          <div className={styles.header}>
            <div className={styles.dropdownContainer}>
              <button
                type="button"
                className={styles.dropdownButton}
                onClick={() => setShowYearDropdown(!showYearDropdown)}
              >
                {currentYear}
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M6 9L12 15L18 9'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>

              {showYearDropdown && (
                <div className={styles.yearDropdown} ref={yearDropdownRef}>
                  {years.map(year => (
                    <button
                      type="button"
                      key={year}
                      className={`${styles.yearOption} ${year === currentYear ? styles.selected : ''}`}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.dropdownContainer}>
              <button
                type="button"
                className={styles.dropdownButton}
                onClick={() => setShowMonthDropdown(!showMonthDropdown)}
              >
                {monthNames[currentMonth]}
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M6 9L12 15L18 9'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>

              {showMonthDropdown && (
                <div className={styles.monthDropdown} ref={monthDropdownRef}>
                  {availableMonths.map(monthIndex => (
                    <button
                      type="button"
                      key={monthIndex}
                      className={`${styles.monthOption} ${monthIndex === currentMonth ? styles.selected : ''}`}
                      onClick={() => handleMonthSelect(monthIndex)}
                    >
                      {monthNames[monthIndex]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Calendar Grid */}
          <div className={styles.calendar}>
            {/* Day headers */}
            <div className={styles.dayHeaders}>
              {weekdayLabels.map((day, i) => (
                <div key={i} className={styles.dayHeader}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days — only show days of the current month (empty cells for padding) */}
            <div className={styles.calendarGrid}>
              {calendarDays.map((week, weekIndex) => (
                  <div key={weekIndex} className={styles.week}>
                    {week.map((day, dayIndex) => (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className={styles.dayWrapper}
                      >
                        {day.isCurrentMonth ? (
                          <button
                            type='button'
                            className={`${styles.day} ${day.isSelected ? styles.selected : ''} ${day.isDisabled ? styles.disabled : ''}`}
                            onClick={() => handleDateSelect(day)}
                            disabled={day.isDisabled}
                          >
                            {day.number}
                          </button>
                        ) : (
                          <span
                            className={styles.dayPlaceholder}
                            aria-hidden='true'
                          />
                        )}
                      </div>
                    ))}
                  </div>
              ))}
            </div>
          </div>
        </div>
        );
        if (inline) return panel;
        if (useMobileSheet) {
          return createPortal(
            <div
              className={styles.sheetOverlay}
              onClick={closeSheet}
              role='presentation'
            >
              <div
                className={styles.sheetPanel}
                role='dialog'
                aria-modal='true'
                aria-label={sheetTitle}
                onClick={event => event.stopPropagation()}
              >
                <div className={styles.sheetHeader}>
                  <span className={styles.sheetTitle}>{sheetTitle}</span>
                  <button
                    type='button'
                    className={styles.sheetClose}
                    onClick={closeSheet}
                    aria-label={t('common.close')}
                  >
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      aria-hidden
                    >
                      <path
                        d='M6 6l12 12M18 6L6 18'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                      />
                    </svg>
                  </button>
                </div>
                {panel}
              </div>
            </div>,
            document.body
          );
        }
        return createPortal(panel, document.body);
      })()}
    </div>
  );
}
